import { SSEParser } from './SSEParser';
import type { SSEMessage } from './SSEParser';
import { SSEEventDispatcher } from './sse-event-dispatcher';
import type {
  AgentExecuteRequest,
  AgentExecuteResponse,
  SkillInfo,
  SearchResponse,
  AgentProfile,
  AgentListResponse,
} from '@/types/agent';

const API_BASE = '/api/agent';

// 6.4-6: 超时策略
// - 默认 30s：对快速 API（listSkills/toggleSkill/closeSession 等）足够
// - execute 显式传入 360s：匹配后端 TimeoutConfig.GLOBAL
// - chatStream 使用自己的 AbortController，不走 request 函数
const DEFAULT_TIMEOUT_MS = 30_000;

async function request<T>(
  path: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // 与 BaseService 保持一致：附带登录态，否则需登录的接口（如 /skills）会返回未登录
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('bongai_token') : null;
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      // detail 兼容字符串与结构化对象（如 /chat/task/discard 400 的 {message, error_code}）
      const detail = error.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail?.message || `Request failed: ${response.status}`;
      throw new Error(msg);
    }

    return response.json();
  } catch (err: any) {
    if (err.name === 'AbortError') {
      throw new Error('请求超时，请检查网络连接或稍后重试');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

function processSSEMessage(msg: SSEMessage): { type: string; content?: string } | null {
  if (msg.data === '[DONE]') {
    return { type: 'done' };
  }

  if (msg.event === 'error') {
    return { type: 'error', content: msg.data };
  }

  try {
    const data = JSON.parse(msg.data);
    return data;
  } catch {
    if (msg.data) {
      return { type: 'text', content: msg.data };
    }
    return null;
  }
}

interface ChatStreamOptions {
  sessionId?: string;
  projectId?: string;
  modelId?: string;
  /** 显式技能调用：后端据此短路 LLM 意图解析，直接路由到指定技能 */
  skillId?: string;
  attachments?: any[];
  references?: any[];
  searchMode?: 'auto' | 'fast' | 'deep';
  agentId?: string;
  onMessageIds?: (ids: { user_msg_id: string; assistant_msg_id: string }) => void;
  // G2-2：心跳透传（后端长静默期每 3s 一跳，连接存活唯一信号），供节点会话看门狗刷新活跃度
  onHeartbeat?: () => void;
  onAgentId?: (agentId: string) => void;
  onThinking?: (content: string) => void;
  onStatus?: (status: string) => void;
  onCollabStatus?: (content: string) => void;
  onIntent?: (intent: { name: string; confidence: number; source: string; params?: Record<string, any> }) => void;
  onSubThinking?: (agentId: string, content: string) => void;
  onSubOutput?: (agentId: string, content: string) => void;
  // SOFT-2 清理（2026-07-24）：已删除 onClarificationRequest（clarification 事件后端从不发送）
  // P2-8/9 清理（2026-07-24）：已删除 onGenerationConfirm（generation_confirm SSE 事件已由 task_ask 统一替代）
  onSearchResult?: (data: any) => void;
  // 任务流改版：新增事件回调
  onTaskStage?: (data: { kind: string; status: string; label: string; detail: string; skill_id: string; skill_name?: string; agent_name?: string; intent?: string }) => void;
  onTaskIntro?: (data: { intro: string; skill_name: string; agent_name: string }) => void;
  onTaskAsk?: (data: any) => void;
  /** 画布节点链路（§5.7 P1-2）：工作流终态完结事件，携带 persist_rules 落库键值 */
  onSkillResult?: (data: { skill_id: string; session_id: string; results: Record<string, string> }) => void;
  /** 画布节点链路（§5.7 P0-1）：为 true 时工作流终态确认后阻断 handoff 自动续跑 */
  stopAfterSkill?: boolean;
  // 中断决策方案（附录 C）：决策透明化事件回调
  /** 收到运行中插话（"评估中…"状态点） */
  onInterruptReceived?: (data: { user_msg_id: string; queue_pos: number }) => void;
  /** 决策气泡（action/reason 可展开） */
  onInterruptDecision?: (data: { action: string; target_skill: string; reason: string; user_reply: string; confidence: number; source_level: string }) => void;
  /** 后续计划列表（待执行/执行中/完成三态） */
  onTaskPlan?: (data: { steps: Array<{ skill_id: string; title: string; status: string; source: string }> }) => void;
  // P1 工具化（10.5）：工具过程状态条
  /** tool_call=开始执行（显示进行中）/ tool_result=完成（ok 时条目消失，失败时降级话术）；
      type 收窄为后端 _push_tool_status 实际协议值（与 ToolStatusPayload 对齐，避免逆变不兼容） */
  onToolStatus?: (data: { type: 'tool_call' | 'tool_result'; tool: string; display: string; ok?: boolean; error_code?: string }) => void;
  // 交互改版（2026-10-14）：尾部轻提醒（未完成任务 + 继续按钮）
  /** chat 回答结束后收到：前端渲染非模态提醒卡，点按钮 = 显式继续意愿 */
  onTaskReminder?: (data: { intent_name: string; display: string }) => void;
  // 放弃状态回写（2026-10-15）：discard_task 终态帧（卡片原位显示已放弃）
  onTaskReminderUpdate?: (data: { intent_name: string; display: string; status: string }) => void;
}

/**
 * §6 决策误判缓解：前端"不是这个意思"纠错标记（与后端 decision_models 对齐）。
 * 发送 DECISION_CORRECTION_MARKER + 原插话文本 → 后端跳过 LLM 决策直接转 ask。
 */
export const DECISION_CORRECTION_MARKER = '__DECISION_CORRECTION__';

export const AgentService = {
  async execute(req: AgentExecuteRequest): Promise<AgentExecuteResponse> {
    // 6.4-6: execute 同步执行技能，需匹配后端 GLOBAL 超时（360s）
    return request('/execute', {
      method: 'POST',
      body: JSON.stringify(req),
    }, 360_000);
  },

  async chatStream(
    messages: { role: string; content: string }[],
    onChunk: (text: string) => void,
    onDone: () => void,
    onError: (error: string) => void,
    options?: ChatStreamOptions
  ): Promise<AbortController> {
    const controller = new AbortController();
    const parser = new SSEParser();
    // P1（会话切换审计修复）：每条流创建独立 dispatcher 实例。
    // 其标签解析缓冲（_subThinkingBuffer/_subOutputBuffer）是实例可变状态——
    // 方案C 允许 A 会话后台流与 B 会话前台流并存，若共用全局单例，
    // 两条流的 [SUB_THINKING]/[SUB_OUTPUT] 跨 chunk 标签会在缓冲区互相
    // 拼接（归属 guard 只保护回调层，保护不了解析层），导致内容串流。
    const dispatcher = new SSEEventDispatcher();

    // 终态帧守卫：[DONE]/error 均只允许触发一次；body 正常关闭但从未
    // 收到终态帧（后端生成器异常静默断开等）时显式走 onError，
    // 避免会话 runState 永久卡在 running（「执行中」）。
    let terminalHandled = false;
    const guardedOnDone = () => {
      if (terminalHandled) return;
      terminalHandled = true;
      onDone();
    };
    const guardedOnError = (error: string) => {
      if (terminalHandled) return;
      terminalHandled = true;
      onError(error);
    };

    try {
      const body: Record<string, any> = {
        messages,
        session_id: options?.sessionId,
        project_id: options?.projectId,
        model_id: options?.modelId,
      };
      if (options?.attachments?.length) {
        body.attachments = options.attachments;
      }
      if (options?.references?.length) {
        body.references = options.references;
      }
      if (options?.searchMode) {
        body.search_mode = options.searchMode;
      }
      if (options?.agentId) {
        body.agent_id = options.agentId;
      }
      if (options?.skillId) {
        body.skill_id = options.skillId;
      }
      if (options?.stopAfterSkill) {
        body.stop_after_skill = true;
      }

      // SSE 长连接不走 request 封装（需手动 AbortController），登录态需自行附带
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('bongai_token') : null;
      const response = await fetch(`${API_BASE}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Stream failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();

      const read = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const sseMessages = parser.feed(chunk);

            for (const msg of sseMessages) {
              const event = processSSEMessage(msg);
              if (!event) continue;
              dispatcher.dispatch(event, options, onChunk, guardedOnDone, guardedOnError);
            }
          }

          const remaining = parser.flush();
          if (remaining) {
            const event = processSSEMessage(remaining);
            if (event) {
              dispatcher.dispatch(event, options, onChunk, guardedOnDone, guardedOnError);
            }
          }

          if (!terminalHandled) {
            guardedOnError('连接已中断且未收到完成信号');
          }
        } catch (err: any) {
          if (err.name !== 'AbortError' && !terminalHandled) {
            guardedOnError(err.message || 'Stream error');
          }
        }
      };

      read();
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        onError(err.message || 'Connection error');
      }
    }

    return controller;
  },

  /**
   * 停止当前会话的后台生成流（终止回流修复 2026-10-12）。
   *
   * abort 仅断开前端 SSE，后端 BackgroundStreamTask 会继续执行并落库
   * 完整结果，轮询/恢复通道会把结果拉回视图。停止按钮需同步调用本方法
   * 通知后端取消任务（后端落库 is_complete=2 中断态，轮询判 failed 不回写）。
   */
  async stopStream(sessionId: string): Promise<{ success: boolean; cancelled: boolean }> {
    return request('/chat/stop', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    }, 5000);
  },

  /**
   * 提醒卡放弃直连（2026-10-15）：丢弃未完成任务存档，绕过 LLM。
   *
   * 此前放弃经消息链路（"放弃「xx」任务"→ LLM 工具循环），产生用户气泡
   * 与"正在思考"等待，且历史上有 LLM 把放弃话术误判为继续意愿的教训。
   * 直调后端复用 discard_task 全部校验，成功即落库终态，前端原位显示
   * 「已放弃」。
   */
  async discardTask(sessionId: string, intentName: string): Promise<{ ok: boolean; discarded?: string; task?: string }> {
    return request('/chat/task/discard', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, intent_name: intentName }),
    }, 8000);
  },

  async listSkills(): Promise<SkillInfo[]> {
    return request('/skills');
  },

  async toggleSkill(skillId: string, enabled: boolean): Promise<void> {
    await request(`/skills/${skillId}/toggle?enabled=${enabled}`, { method: 'POST' });
  },

  async submitFeedback(messageId: string, sessionId: string, rating: number, comment?: string, category?: string): Promise<void> {
    await request('/feedback', {
      method: 'POST',
      body: JSON.stringify({
        message_id: messageId,
        session_id: sessionId,
        rating,
        comment,
        category,
      }),
    });
  },

  /**
   * 工作流阻塞式询问回复
   * 当后端 workflow 步骤需要用户输入时，调用此接口回复，SSE 流保持打开并继续执行。
   *
   * P0-D2: 新增 assistantMsgId 可选参数，显式指定要清除哪条消息的 task_ask_pending，
   * 避免多 tab 场景下后端误清新消息字段。
   *
   * 方案 A: 新增 attachments/references 可选参数，支持在弹窗中上传文件（如竞品图）。
   * 后端 /workflow_reply 会把附件持久化到 user 消息并携带到 Future 解除值，
   * skill_executor 据此把图片注入 ctx.messages 让 LLM 能"看见"用户上传的图。
   */
  async workflowReply(
    sessionId: string,
    reply: string,
    assistantMsgId?: string,
    attachments: Array<Record<string, any>> = [],
    references: Array<Record<string, any>> = [],
    displayText?: string,
    clientTiming?: Record<string, number>,
  ): Promise<{ success: boolean; message?: string }> {
    return request('/workflow_reply', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        reply,
        assistant_msg_id: assistantMsgId,
        attachments,
        references,
        // 修复3: 传递 displayText（用户点击的选项标签），后端存入 workflow_qa_pairs.answer
        // 若不传，后端回退到 reply（兼容旧调用方）
        display_text: displayText,
        // 诊断埋线：弹窗交互耗时（毫秒），后端仅记日志，用于定位空窗期归属
        client_timing: clientTiming,
      }),
    });
  },

  /**
   * ASK-GAP-6: 工作流取消接口。
   *
   * 与 workflowReply('忽略') 区分：
   * - workflowReply 会将 '忽略' 作为真实用户输入传给 LLM，LLM 可能继续生成内容；
   * - workflowCancel 直接取消挂起的 Future，后端不再继续工作流，前端 SSE 流被显式中止。
   */
  async workflowCancel(
    sessionId: string,
  ): Promise<{ success: boolean; message?: string }> {
    return request('/workflow_cancel', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });
  },

  /**
   * 中断决策方案（§3.1.1 / §3.9）：运行中插话接口。
   *
   * 任务运行中（isLoading）时前端自动改走本端点（对调用方透明）：
   * - injected：消息注入运行中任务，决策帧经既有 SSE 流透出；
   * - no_active_task / disabled：返回 fallback='chat_stream'，调用方回落常规发送；
   * - queue_full / duplicate：带用户可见提示。
   */
  async sendInterrupt(
    sessionId: string,
    message: string,
    attachments: Array<Record<string, any>> = [],
    references: Array<Record<string, any>> = [],
  ): Promise<{
    status: 'injected' | 'no_active_task' | 'queue_full' | 'duplicate' | 'disabled';
    user_msg_id?: string;
    persisted?: boolean;
    decision?: string;
    message?: string;
    fallback?: string;
  }> {
    return request('/chat/interrupt', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        message,
        attachments,
        references,
      }),
    });
  },

  /**
   * 生成参数确认回复（挂起模型，§4.1）。
   *
   * 后端解除挂起协程的 confirm Future 并续跑；返回语义：
   * - suspended=true：协程命中并已恢复续跑（多 tab 先到者）
   * - already=true：该消息已被其他 tab 确认（后到者静默，不降级）
   * - suspended=false 且 already=false：协程不在（重启/超时/旧数据）→ 前端降级 /execute
   */
  async generationConfirmReply(args: {
    sessionId: string;
    assistantMsgId: string;
    action?: 'confirm' | 'cancel';
    generationParams?: Record<string, any>;
    inputImages?: string[];
    attachments?: Array<Record<string, any>>;
    canvasNodeMap?: Record<string, string>;
    primaryNodeId?: string;
    displayText?: string;
    clientTiming?: Record<string, number>;
  }): Promise<{ success: boolean; suspended?: boolean; already?: boolean; message?: string }> {
    return request('/generation_confirm_reply', {
      method: 'POST',
      body: JSON.stringify({
        session_id: args.sessionId,
        assistant_msg_id: args.assistantMsgId,
        action: args.action ?? 'confirm',
        generation_params: args.generationParams ?? {},
        input_images: args.inputImages ?? [],
        attachments: args.attachments ?? [],
        canvas_node_map: args.canvasNodeMap ?? {},
        primary_node_id: args.primaryNodeId ?? null,
        display_text: args.displayText ?? null,
        client_timing: args.clientTiming ?? null,
      }),
    });
  },

  /**
   * 生成参数确认取消（§4.1）。
   *
   * 语义与 workflowCancel 对齐：真正取消后端挂起 Future（graceful cancel 标记），
   * 非仅隐藏本地弹窗。返回 cancelled=false 表示 Future 不在（已确认在途或协程不在），
   * 调用方按 already_resolved 容忍处理，不得触发降级重放。
   */
  async generationConfirmCancel(
    sessionId: string,
    assistantMsgId: string,
  ): Promise<{ success: boolean; cancelled?: boolean }> {
    return request('/generation_confirm_reply', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        assistant_msg_id: assistantMsgId,
        action: 'cancel',
      }),
    });
  },

  // P3-4: search 方法已移除，搜索功能统一通过 chatStream 流式路径执行
  // 使用 SearchOrchestrator 作为唯一搜索入口

  async createSession(projectId: string): Promise<{ sessionId: string; projectId: string; status: string }> {
    return request('/sessions', {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId }),
    });
  },

  async getSessionMessages(sessionId: string, limit: number = 50): Promise<{
    sessionId: string;
    messages: Array<{
      id: string;
      sessionId: string;
      role: string;
      content: string;
      type: string;
      isComplete?: number;
      sequence?: number;
      createdAt: string;
      updatedAt?: string;
      metadata?: Record<string, any>;
      introText?: string;
      processLogs?: any[];
      attachments?: any[];
      references?: any[];
    }>;
    total: number;
    messageCount?: number;
    version?: number;
    // P2-4: 后端恢复会话时返回当前绑定的 agentId（用于前端恢复 agent 选择）
    currentAgentId?: string;
  }> {
    return request(`/sessions/${sessionId}/messages?limit=${limit}`);
  },

  /**
   * V7-NEW-5: 物理删除会话（级联删除所有消息）
   *
   * 替代 closeSession 软关闭场景下的"删除历史"调用。
   * 后端 DELETE /sessions/{id} 会级联物理删除该 session 的所有 agent_messages，
   * 避免 agent_messages 表无限膨胀。
   *
   * 返回值含 messagesDeleted 字段，供 UI 反馈删除了多少条消息。
   */
  async deleteSession(sessionId: string): Promise<{
    success: boolean;
    sessionId: string;
    messagesDeleted: number;
  }> {
    return request(`/sessions/${sessionId}`, { method: 'DELETE' });
  },

  async updateMessageMetadata(
    messageId: string,
    sessionId: string,
    metadata: Record<string, any>,
    // 🔴 v6-#3: 支持原子更新 content 和 msg_type，避免两次 API 调用之间的数据不一致
    // 收敛（双存储消除）：task_snapshot/intro_text/process_logs 为专用列显式字段，
    // 不再塞进 metadata dict 由后端提取转换。undefined=跳过更新。
    extra?: {
      content?: string;
      msg_type?: string;
      metadata_deletes?: string[];
      task_snapshot?: Record<string, any>;
      intro_text?: string;
      process_logs?: any[];
    }
  ): Promise<{ success: boolean; messageId: string }> {
    return request('/messages/metadata', {
      method: 'POST',
      body: JSON.stringify({
        message_id: messageId,
        session_id: sessionId,
        metadata,
        ...(extra?.content !== undefined ? { content: extra.content } : {}),
        ...(extra?.msg_type !== undefined ? { msg_type: extra.msg_type } : {}),
        ...(extra?.metadata_deletes ? { metadata_deletes: extra.metadata_deletes } : {}),
        ...(extra?.task_snapshot !== undefined ? { task_snapshot: extra.task_snapshot } : {}),
        ...(extra?.intro_text !== undefined ? { intro_text: extra.intro_text } : {}),
        ...(extra?.process_logs !== undefined ? { process_logs: extra.process_logs } : {}),
      }),
    });
  },

  // P3-F2: deleteMessage 已删除（死代码，0 个调用方）
  // 如需删除消息，应使用 deleteSession 级联删除整个会话

  async updateMessageContent(
    messageId: string,
    sessionId: string,
    content: string,
    type?: string
  ): Promise<{ success: boolean; messageId: string }> {
    const body: Record<string, any> = { session_id: sessionId, content };
    if (type) body.type = type;
    return request(`/messages/${messageId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async getStreamStatus(sessionId: string, assistantMsgId?: string): Promise<{
    state: string;
    taskId?: string;
    content?: string;
    contentLength?: number;
    isComplete?: number;
    error?: string;
    processLogs?: any[];
    introText?: string;
    // P1-27: 补全后端实际返回字段（原由调用方 as any 访问，破坏类型安全）
    // msgType / type: 后端消息类型（如 'text' / 'search' / 'generation_confirm'）
    // 注：两者为同义字段，msgType 优先，type 兜底（与 useStreamPolling 的回退顺序一致）
    msgType?: string;
    type?: string;
    /** 后端 metadata（pending_* 等辅助字段，供 restoreBackendMessage 恢复） */
    metadata?: Record<string, any>;
    /** P0-F3: task_snapshot 专用列（收敛后单存储，回填迁移保证列完备），供轮询完成重建 */
    taskSnapshot?: Record<string, any>;
  }> {
    const params = new URLSearchParams();
    params.set('session_id', sessionId);
    if (assistantMsgId) params.set('assistant_msg_id', assistantMsgId);
    return request(`/chat/stream-status?${params.toString()}`);
  },

  async getIncompleteMessages(sessionId: string): Promise<{
    messages: Array<{
      id: string;
      content: string;
      isComplete: number;
      type: string;
      updatedAt: string | null;
    }>;
  }> {
    return request(`/sessions/${sessionId}/incomplete-messages`);
  },

  async listSessions(projectId?: string, limit: number = 20): Promise<{
    sessions: Array<{
      sessionId: string;
      projectId: string;
      status: string;
      messageCount?: number;
      version?: number;
      topic?: string;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    params.set('limit', String(limit));
    return request(`/sessions?${params.toString()}`);
  },

  async listAgents(): Promise<AgentListResponse> {
    return request('/agents');
  },

  async getAgent(agentId: string): Promise<AgentProfile> {
    return request(`/agents/${agentId}`);
  },

  // P3-F2: reloadAgents 已删除（死代码，0 个调用方）
  // 如需重载 agent 配置，重启后端服务即可
};
